import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSupabaseClient } from '../lib/supabase';
import {
  eventAllowsGuestExpansion,
  getEventLifecycleMessage,
} from '../domain/eventLifecycle';
import { loadEventForUser } from './events';
import type { WebEvent } from '../types/events';

const EVENT_CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export type CreateWebEventInput = {
  name: string;
  description?: string;
  locationName?: string;
  locationAddress?: string;
  startTimeIso: string;
  endTimeIso: string;
  isPublic: boolean;
  allowGuestDownloads: boolean;
  joinType: 'open' | 'code' | 'verified';
};

export type WebJoinResult = {
  status: 'joined' | 'already_member' | 'requested';
  eventId: string;
  eventName: string;
  event: WebEvent | null;
  message: string;
};

export type WebNotification = {
  id: string;
  user_id: string;
  type: string;
  event_id?: string | null;
  actor_id?: string | null;
  title?: string | null;
  body?: string | null;
  data?: Record<string, unknown> | null;
  read?: boolean | null;
  created_at: string;
  event?: { id: string; name: string } | null;
  actor?: { id: string; full_name?: string | null } | null;
};

export type WebProfile = {
  id: string;
  full_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
};

function generateCode(length: number): string {
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => EVENT_CODE_CHARS[value % EVENT_CODE_CHARS.length]).join('');
}

async function generateUniqueEventCode(): Promise<string> {
  const client = getSupabaseClient();
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const code = generateCode(6);
    const { data, error } = await client.from('events').select('id').eq('code', code).maybeSingle();
    if (error) throw error;
    if (!data) return code;
  }
  throw new Error('Could not create a unique event code. Please try again.');
}

export async function createWebEvent(
  input: CreateWebEventInput,
  userId: string,
): Promise<WebEvent> {
  const client = getSupabaseClient();
  const name = input.name.trim();
  const description = String(input.description ?? '').trim();
  const locationName = String(input.locationName ?? '').trim();
  const locationAddress = String(input.locationAddress ?? '').trim();
  const start = new Date(input.startTimeIso);
  const end = new Date(input.endTimeIso);

  if (!name) throw new Error('Please give your event a name.');
  if (Number.isNaN(start.getTime())) throw new Error('Please choose an event start date and time.');
  if (Number.isNaN(end.getTime())) throw new Error('Please choose an event end date and time.');
  if (end.getTime() <= start.getTime()) {
    throw new Error('Event end date and time must be after the start date and time.');
  }

  const code = await generateUniqueEventCode();
  const { data, error } = await client.rpc('create_event_for_current_user', {
    p_name: name,
    p_description: description || null,
    p_code: code,
    p_location_lat: null,
    p_location_lng: null,
    p_location_address: locationAddress || null,
    p_location_name: locationName || null,
    p_auto_upload_enabled: false,
    p_is_public: input.isPublic,
    p_join_type: input.isPublic ? input.joinType : 'code',
    p_allow_guest_downloads: input.allowGuestDownloads,
    p_start_time: start.toISOString(),
    p_end_time: end.toISOString(),
  });
  if (error) throw error;

  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
  const eventId = String(row?.id ?? '');
  if (!eventId) throw new Error('Event was not created. Please try again.');

  const loaded = await loadEventForUser(eventId, userId);
  if (loaded) return loaded;
  return { ...(row as unknown as Omit<WebEvent, 'access_role'>), access_role: 'organizer' } as WebEvent;
}

function cleanJoinCode(value: string): string {
  return String(value ?? '')
    .replace(/[^A-Z0-9]/gi, '')
    .toUpperCase()
    .slice(0, 8);
}

async function bestEffortOrganizerPush(params: {
  organizerId: string;
  requesterName: string;
  eventId: string;
  eventName: string;
}) {
  try {
    const client = getSupabaseClient();
    const { error } = await client.functions.invoke('send-push-notification', {
      body: {
        toUserId: params.organizerId,
        title: `New join request — ${params.eventName}`,
        body: `${params.requesterName || 'Someone'} wants to join your event.`,
        data: { screen: 'guests', eventId: params.eventId },
      },
    });
    if (error) console.warn('[web join] organizer push failed:', error.message);
  } catch (error) {
    console.warn('[web join] organizer push failed:', error);
  }
}

export async function joinEventFromWeb(params: {
  rawCode: string;
  userId: string;
  requesterName?: string | null;
}): Promise<WebJoinResult> {
  const client = getSupabaseClient();
  const code = cleanJoinCode(params.rawCode);
  if (![6, 8].includes(code.length)) {
    throw new Error('Please enter a valid 6-character event code or 8-character invite code.');
  }

  const { data: lookupData, error: lookupError } = await client.rpc('lookup_join_target', {
    p_code: code,
  });
  if (lookupError) throw lookupError;

  const target = (lookupData ?? {}) as { event?: Record<string, any> | null; invite?: Record<string, any> | null; reason?: string | null };
  const event = target.event ?? null;
  const invite = target.invite ?? null;
  if (!event) {
    throw new Error(
      target.reason === 'invite_not_found'
        ? 'Invite not found or already used. Please check the invite code.'
        : 'Event not found. Please check the code and try again.',
    );
  }

  if (!eventAllowsGuestExpansion(event)) {
    throw new Error(getEventLifecycleMessage(event) ?? 'This event is closed. Ask the host to reopen it.');
  }
  if (
    event.expires_at &&
    new Date(event.expires_at).getTime() < Date.now() &&
    event.expire_action === 'archive'
  ) {
    throw new Error('This event has expired and is now archived.');
  }

  if (event.organizer_id === params.userId) {
    const loaded = await loadEventForUser(String(event.id), params.userId);
    return {
      status: 'already_member',
      eventId: String(event.id),
      eventName: String(event.name ?? 'Event'),
      event: loaded,
      message: 'You already own this event.',
    };
  }

  const { data: blockedRow, error: blockedError } = await client
    .from('event_blocked_users')
    .select('event_id')
    .eq('event_id', event.id)
    .eq('blocked_user_id', params.userId)
    .maybeSingle();
  if (blockedError && !String(blockedError.message || '').includes('does not exist')) throw blockedError;
  if (blockedRow) {
    throw new Error('You cannot join this event because a host or co-admin removed your access.');
  }

  const { data: existing, error: existingError } = await client
    .from('event_members')
    .select('event_id')
    .eq('event_id', event.id)
    .eq('user_id', params.userId)
    .maybeSingle();
  if (existingError) throw existingError;

  if (existing) {
    const loaded = await loadEventForUser(String(event.id), params.userId);
    return {
      status: 'already_member',
      eventId: String(event.id),
      eventName: String(event.name ?? 'Event'),
      event: loaded,
      message: `You're already part of “${String(event.name ?? 'this event')}”.`,
    };
  }

  if (event.join_type === 'verified' && !invite) {
    const { data: existingRequest, error: requestLookupError } = await client
      .from('join_requests')
      .select('id,status')
      .eq('event_id', event.id)
      .eq('user_id', params.userId)
      .maybeSingle();
    if (requestLookupError) throw requestLookupError;

    if (existingRequest?.status === 'pending') throw new Error('You already have a pending request for this event.');
    if (existingRequest?.status === 'denied') throw new Error('Your previous request was not approved.');

    const { error: insertRequestError } = await client.from('join_requests').insert({
      event_id: event.id,
      user_id: params.userId,
    });
    if (insertRequestError) throw insertRequestError;

    const { error: notificationError } = await client.from('notifications').insert({
      user_id: event.organizer_id,
      type: 'join_request',
      event_id: event.id,
      actor_id: params.userId,
    });
    if (notificationError) console.warn('[web join] notification log failed:', notificationError.message);

    void bestEffortOrganizerPush({
      organizerId: String(event.organizer_id),
      requesterName: String(params.requesterName ?? 'Someone'),
      eventId: String(event.id),
      eventName: String(event.name ?? 'Event'),
    });

    return {
      status: 'requested',
      eventId: String(event.id),
      eventName: String(event.name ?? 'Event'),
      event: null,
      message: `Your request to join “${String(event.name ?? 'this event')}” was sent to the organizer.`,
    };
  }

  if (invite?.invite_code) {
    const { error } = await client.rpc('accept_event_invite', { p_invite_code: invite.invite_code });
    if (error) throw error;
  } else {
    const { error } = await client.rpc('join_event_by_code', { p_code: code });
    if (error) throw error;
  }

  const loaded = await loadEventForUser(String(event.id), params.userId);
  return {
    status: 'joined',
    eventId: String(event.id),
    eventName: String(event.name ?? 'Event'),
    event: loaded,
    message: `You joined “${String(event.name ?? 'this event')}”.`,
  };
}

function normalizeRelation<T>(value: T | T[] | null | undefined): T | null {
  return Array.isArray(value) ? value[0] ?? null : value ?? null;
}

export async function loadWebNotifications(userId: string): Promise<WebNotification[]> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('notifications')
    .select(`
      id,user_id,type,event_id,actor_id,title,body,data,read,created_at,
      event:events(id,name),
      actor:profiles!notifications_actor_id_fkey(id,full_name)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;

  const rows = ((data ?? []) as unknown as WebNotification[]).map((row) => ({
    ...row,
    event: normalizeRelation(row.event),
    actor: normalizeRelation(row.actor),
  }));

  if (rows.some((row) => !row.read)) {
    const { error: readError } = await client
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false);
    if (readError) console.warn('[web notifications] mark read failed:', readError.message);
  }
  return rows;
}

export async function clearWebNotifications(userId: string): Promise<void> {
  const client = getSupabaseClient();
  const { error } = await client.from('notifications').delete().eq('user_id', userId);
  if (error) throw error;
}

export function subscribeToWebNotifications(userId: string, onChange: () => void): () => void {
  const client = getSupabaseClient();
  let channel: RealtimeChannel = client
    .channel(`web-notifications-${userId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
      onChange,
    );
  channel = channel.subscribe();
  return () => {
    void client.removeChannel(channel);
  };
}

export async function loadWebProfile(userId: string): Promise<WebProfile | null> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('profiles')
    .select('id,full_name,email,avatar_url')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw error;
  return (data as WebProfile | null) ?? null;
}

export async function saveWebProfileName(userId: string, fullName: string): Promise<WebProfile> {
  const trimmed = fullName.trim();
  if (trimmed.length < 2 || trimmed.includes('@')) {
    throw new Error('Please enter your first and last name or a display name.');
  }
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('profiles')
    .update({ full_name: trimmed })
    .eq('id', userId)
    .select('id,full_name,email,avatar_url')
    .single();
  if (error) throw error;
  return data as WebProfile;
}
